import {
  type RefCallback,
  type ReactNode,
  type RefObject,
  createContext,
  useContext,
  useMemo,
  useRef,
  useState,
  startTransition,
  useCallback,
} from 'react'
import {
  type MotionValue,
  useMotionTemplate,
  useTransform,
  LayoutGroup,
  LazyMotion,
  useScroll,
  m,
  easeOut,
  useMotionValue,
  useMotionValueEvent,
  AnimatePresence,
} from 'motion/react'
import { composeRefs, useComposedRefs } from '@radix-ui/react-compose-refs'
import { useElementSize } from '@/hooks/element-hooks'
import { type PressEventHandler } from '@/infra/prop-types'
import cx from 'classnames'
import styles from './ScreenHeader.module.css'
import { usePageTopOffset } from '@/contexts/page-top-offset-context'
import { match } from '@/utils/pattern-match-util'
import { useProvideScrollPaddingTop } from '@/contexts/scroll-padding-context'
import { useIsSkeleton } from '@/components/Skeleton'
import { Button } from '../Button'
import { useCssVariable } from '@/features/shell/theming/use-css-variable'
import { UNSAFE_PortalProvider } from 'react-aria'

const loadMotionFeatures = () =>
  import('@/infra/motion-features').then((r) => r.default)

/** @see {ScreenHeaderProps.scrollFading} */
type ScrollFading = 'auto' | 'always' | 'off'

/** @see {ScreenHeaderProps.fadeVariant} */
type ScrollFadeVariant = 'default' | 'aggressive'

interface ScreenHeaderProps {
  children?: ReactNode

  /**
   * When `true`, the top bar will overlay the content that follows, based on
   * the size of the top bar's content.
   */
  isOverlay?: boolean

  /**
   * Offset from the top in pixels to stick to.
   * This must effectively be the height of any element(s) preceding the header,
   * which MUST be in-flow, and should have a z-index
   * of `--z-index-content` or higher to ensure overflow is clipped.
   *
   * NOTE: The page top offset context is already being used - this
   * can be used to override it.
   */
  topOffset?: number

  /**
   * Controls the scrolling fade effect.
   * - `auto`: Fades while content is in viewport. When the content has left the
   *    viewport, the background of the top bar becomes solid.
   * - `always`: Always applies the scroll fading effect.
   * - `off`: Never applies the scroll fading effect.
   */
  scrollFading?: ScrollFading

  /**
   * When using scroll fading, determines which variant to use.
   * The default variant allows more content to appear behind, while the
   * aggressive one will cut off content more abruptly to make sure the
   * top bar title does not get mixed up with the content underneath.
   */
  fadeVariant?: ScrollFadeVariant

  /**
   * Offset the title scroll progress (which is used for animations).
   */
  titleScrollProgressOffset?: number

  /**
   * Offset the content scroll progress (which is used for animations).
   */
  contentScrollProgressOffset?: number

  /**
   * Ref to use for the viewport. Defaults to the window.
   */
  viewportRef?: RefObject<HTMLElement | null>

  /**
   * Whether to apply scroll-padding-top to the document based on
   * the presence of the header.
   */
  applyDocumentScrollPadding?: boolean

  /**
   * The safe area inset at the top of the screen.
   */
  safeAreaInsetTop?: number
}

/**
 * Manages a header to be used at the top of the screen.
 * This component consumes the page top offset context.
 * This component itself does not render any DOM; it only manages the state
 * consumed by the child components.
 *
 * Anatomy:
 *
 * @example
 *
 *  <ScreenHeaderProvider>
 *    <ScreenHeaderPersistentTopBar>
 *      <ScreenHeaderPersistentTopBarLayout>
 *        <ScreenHeaderPersistentTopBarTitle/>
 *      </ScreenHeaderPersistentTopBarLayout>
 *    </ScreenHeaderPersistentTopBar>
 *    <ScreenHeaderMain>
 *      <ScreenHeaderContent>
 *        <ScreenHeaderTitle/>
 *      </ScreenHeaderContent>
 *      <ScreenHeaderBorder />
 *    </ScreenHeaderMain>
 *  </ScreenHeaderProvider>
 */
export function ScreenHeaderProvider({
  children,
  viewportRef,
  topOffset,
  applyDocumentScrollPadding,
  titleScrollProgressOffset = 0,
  contentScrollProgressOffset = 0,
  isOverlay = false,
  scrollFading = 'auto',
  fadeVariant = 'default',
}: ScreenHeaderProps) {
  const titleRef = useRef<HTMLElement>(null)
  const contentRef = useRef<HTMLElement>(null)
  const pageTopOffset = usePageTopOffset()
  const safeAreaInsetTop = parseInt(
    useCssVariable('--app-safe-area-inset-top').replace('px', '') || '0',
    10,
  )

  topOffset ??= pageTopOffset
  topOffset += safeAreaInsetTop

  const {
    connectRef: connectTopBarRef,
    size: { height: topBarHeight },
  } = useElementSize()
  const {
    connectRef: connectContentRef,
    size: { height: contentHeight },
  } = useElementSize()
  const {
    connectRef: connectTitleRef,
    size: { height: titleHeight },
  } = useElementSize()
  const {
    connectRef: connectMainRef,
    size: { height: mainHeight },
  } = useElementSize()

  const { scrollY: contentScrollY } = useScroll({
    container: viewportRef,
    target: contentRef,
    offset: ['start start', `end ${topBarHeight}px`],
  })

  const { scrollY: titleScrollY } = useScroll({
    container: viewportRef,
    target: titleRef,
    offset: ['start start', `end ${topBarHeight}px`],
  })

  const titleScrollProgress = useElementScrollProgressInternal(
    titleScrollY,
    titleHeight,
    topBarHeight,
    isOverlay,
    titleScrollProgressOffset,
  )
  const contentScrollProgress = useElementScrollProgressInternal(
    contentScrollY,
    contentHeight,
    topBarHeight,
    isOverlay,
    contentScrollProgressOffset,
  )

  const context = useMemo((): ScreenHeaderContextValue => {
    const topBarOffset = topBarHeight + topOffset
    // NOTE: the `mainHeight` takes into account the top bar.
    const totalOffset =
      (isOverlay ? topBarOffset : topOffset) + mainHeight - contentHeight
    return {
      contentHeight,
      contentScrollY,
      contentScrollProgress,
      fadeVariant,
      isOverlay,
      scrollFading,
      mainHeight,
      titleHeight,
      titleScrollY,
      titleScrollProgress,
      topOffset,
      totalOffset,
      topBarHeight,
      topBarOffset,
      viewportRef,
      safeAreaInsetTop,
      connectTopBarRef,
      connectMainRef,
      connectTitleRef: composeRefs(titleRef, connectTitleRef),
      connectContentRef: composeRefs(contentRef, connectContentRef),
    }
  }, [
    topBarHeight,
    topOffset,
    mainHeight,
    contentHeight,
    contentScrollY,
    contentScrollProgress,
    fadeVariant,
    isOverlay,
    scrollFading,
    titleHeight,
    titleScrollY,
    titleScrollProgress,
    viewportRef,
    safeAreaInsetTop,
    connectTopBarRef,
    connectMainRef,
    connectTitleRef,
    connectContentRef,
  ])

  useProvideScrollPaddingTop(
    applyDocumentScrollPadding ? context.totalOffset + 3 : null,
  )

  return (
    <LazyMotion features={loadMotionFeatures}>
      <ScreenHeaderContext.Provider value={context}>
        {children}
      </ScreenHeaderContext.Provider>
    </LazyMotion>
  )
}

interface ScreenHeaderMainProps {
  children?: ReactNode
}

/**
 * Main screen header area.
 * Should be rendered inside a {@link ScreenHeaderProvider} and
 * below any {@link ScreenHeaderPersistentTopBar}.
 */
export function ScreenHeaderMain({ children }: ScreenHeaderMainProps) {
  const {
    contentHeight,
    scrollFading,
    isOverlay,
    topBarHeight,
    topOffset,
    safeAreaInsetTop,
    connectMainRef,
  } = useScreenHeaderContext()

  // If we're always scroll-fading, the content needs to scroll up
  // past the top bar rather than stop scrolling once it reaches it.
  const scrollFadeAdjustment =
    scrollFading === 'always' || scrollFading === 'off'
      ? -topBarHeight + topOffset
      : 0
  const overlayAdjustment = isOverlay ? topBarHeight : 0

  const top =
    topOffset - contentHeight + scrollFadeAdjustment + overlayAdjustment

  return (
    <m.div
      ref={connectMainRef}
      className={styles.main}
      layoutRoot
      style={{ top, marginTop: safeAreaInsetTop }}
    >
      {children}
    </m.div>
  )
}

interface ScreenHeaderPersistentTopBarProps {
  children?: ReactNode

  /**
   * When to start the fade effect.
   * - `always`: the fade will always be visible. This is the default.
   * - `title-exit`: the fade appears once the title is near exiting
   *    the viewport. If {@link ScreenHeaderProps#isOverlay} is enabled,
   *    this might be a better option.
   */
  fadeWhen?: 'always' | 'title-exit'
}

/**
 * A bar that persists at the top of the screen when scrolled.
 */
export function ScreenHeaderPersistentTopBar({
  children,
  fadeWhen = 'always',
}: ScreenHeaderPersistentTopBarProps) {
  const {
    connectTopBarRef,
    contentHeight,
    contentScrollY,
    fadeVariant,
    isOverlay,
    scrollFading,
    titleScrollY,
    topOffset,
    topBarHeight,
    topBarOffset,
  } = useScreenHeaderContext()

  const gradientOpacity = useTransform(
    titleScrollY,
    fadeWhen === 'always' ? [1] : [0, 20],
    fadeWhen === 'always' ? [1] : [0, 1],
  )

  // Final gradient layer should fade in at the very end of
  // content's end reaching the top bar
  const start = contentHeight - 10 - (isOverlay ? topBarOffset : 0)
  const end = contentHeight - (isOverlay ? topBarOffset : 0)

  const overlayGradientOpacity = useTransform(
    contentScrollY,
    // Prevent the gradient from starting at full opacity on first render.
    [start < 0 ? 0 : start, end < 1 ? 100 : end],
    [0, 100],
    { ease: easeOut },
  )

  // This gradient overlay is used to make the bottom part of the main gradient
  // fully opaque once we've reached the bottom part of the content
  // during scroll. This is because the main content actually won't scroll
  // further due to being `sticky`, and fading it out completely gives the
  // illusion that it'll keep scrolling up.
  const overlayGradient = useMotionTemplate`
    linear-gradient(
      to bottom,
      var(--surface-primary) 0%,
      color-mix(in srgb, var(--surface-primary) ${overlayGradientOpacity}%, transparent)
    )
  `

  const ref = useRef<HTMLElement>(null)
  const composedRef = useComposedRefs(ref, connectTopBarRef)
  const getPortalContainer = useCallback(() => ref.current, [])
  return (
    <ScreenHeaderTopBarPortalContainerContext value={getPortalContainer}>
      <m.div
        layoutRoot
        layout="preserve-aspect"
        ref={composedRef}
        className={cx(styles.topBarContainer)}
        style={{
          // The top bar itself is transparent by default, the background
          // color is provided by the content which will be pulled up
          // to match.
          marginBottom: -topBarHeight,
          top: topOffset,
        }}
      >
        {scrollFading !== 'off' && (
          <>
            <m.div
              className={cx(
                styles.fadingGradient,
                fadeVariant === 'aggressive' && styles.aggressive,
              )}
              style={{
                opacity: gradientOpacity,
                height: topBarHeight,
                // Pull up the following elements so they go underneath
                // the top bar. The content adds top padding to accommodate.
                marginBottom: -topBarHeight,
              }}
            />
            {scrollFading !== 'always' && (
              <m.div
                className={styles.fadingGradient}
                style={{
                  opacity: gradientOpacity,
                  height: topBarHeight,
                  background: overlayGradient,
                  // Pull up the following elements so they go underneath
                  // the top bar. The content adds top padding to accommodate.
                  marginBottom: -topBarHeight,
                }}
              />
            )}
          </>
        )}

        {children}
      </m.div>
    </ScreenHeaderTopBarPortalContainerContext>
  )
}

interface ScreenHeaderPersistentTopBarLayoutProps {
  children?: ReactNode

  /**
   * Element on the left side of the bar.
   */
  left?: ReactNode

  /**
   * Element on the right side of the bar.
   */
  right?: ReactNode

  /**
   * Determines when the top bar layout is visible.
   * - `always`: Default, always visible.
   * - `title-exit`: Fades in based on title scroll progress.
   * - `content-exit`: Fades in based on content scroll progress
   */
  visibility?: 'always' | 'title-exit' | 'content-exit'

  /**
   * The layout to use.
   * - `normal`: Default; ensures the layout has a minimum height based on the design.
   * - `auto`: does not enforce a minimum height.
   */
  layout?: 'normal' | 'auto'
}

/**
 * Common layout for the persistent top bar, allocating 2 side slots for
 * icons and a main one for a {@link ScreenHeaderPersistentTopBarTitle}.
 */
export function ScreenHeaderPersistentTopBarLayout({
  left,
  right,
  children,
  visibility = 'always',
  layout = 'normal',
}: ScreenHeaderPersistentTopBarLayoutProps) {
  const opacityProgress = useTopBarElementScrollProgress(
    match(visibility, {
      'title-exit': 'title',
      'content-exit': 'content',
      [match.DEFAULT]: () => null,
    }),
  )

  // When nearly transparent, disable interactions.
  const [inert, setInert] = useState(opacityProgress.get() < 0.2)
  useMotionValueEvent(
    useTransform(opacityProgress, [0, 0.2], [true, false]),
    'change',
    useCallback((v) => startTransition(() => setInert(v)), []),
  )

  const opacity = useTransform(() =>
    visibility === 'always' ? 1 : opacityProgress.get(),
  )

  return (
    <div
      className={cx(
        styles.topBarLayoutContainer,
        layout === 'normal' && styles.normal,
      )}
    >
      <m.div
        inert={visibility === 'always' ? false : inert}
        className={cx(styles.topBarLayout)}
        style={{
          opacity,
        }}
      >
        {left && <m.div className={styles.topBarLayoutLeft}>{left}</m.div>}
        <m.div className={styles.topBarLayoutContent}>{children}</m.div>
        {right && <m.div className={styles.topBarLayoutRight}>{right}</m.div>}
      </m.div>
    </div>
  )
}

export interface ScreenHeaderPersistentTopBarTitleProps {
  children?: ReactNode

  /**
   * When the title should be visible.
   * - `title-exit`: becomes visible when the title is exiting the viewport.
   *    This is the default.
   * - `content-exit`: becomes visible when the content exits.
   * - `always`: always visible
   */
  visibility?: 'title-exit' | 'content-exit' | 'always'

  /**
   * Action to display below the title, used when scrolling.
   * Use {@link ScreenHeaderPersistentTopBarAction}.
   */
  action?: ReactNode

  /**
   * If specified, the text will cross-fade
   */
  textAnimationKey?: string
}

/**
 * Title used only when scrolled past the primary header content.
 */
export function ScreenHeaderPersistentTopBarTitle({
  children,
  action,
  textAnimationKey,
  visibility = 'title-exit',
}: ScreenHeaderPersistentTopBarTitleProps) {
  const { titleScrollProgress, contentScrollProgress } =
    useScreenHeaderContext()

  const opacity = useTransform(() =>
    visibility === 'title-exit'
      ? titleScrollProgress.get()
      : visibility === 'content-exit'
        ? contentScrollProgress.get()
        : 1,
  )

  return (
    <LayoutGroup>
      <m.div className={styles.topBarTitleContainer}>
        <m.h1
          // The title will be available elsewhere on the page
          aria-hidden
          className={styles.topBarTitle}
          style={{
            opacity,
          }}
        >
          {textAnimationKey ? (
            <AnimatePresence initial={false} mode="wait">
              <m.span
                key={textAnimationKey}
                layout={!!textAnimationKey}
                transition={{ duration: 0.1 }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                {children}
              </m.span>
            </AnimatePresence>
          ) : (
            children
          )}
        </m.h1>
        {action}
      </m.div>
    </LayoutGroup>
  )
}

interface ScreenHeaderPersistentTopBarActionProps {
  label: string
  onPress?: PressEventHandler
  isDisabled?: boolean
  isSkeleton?: boolean
  testId?: string
}

/**
 * An action to display in the persistent top bar when scrolled.
 */
export function ScreenHeaderPersistentTopBarAction({
  label,
  onPress,
  isDisabled,
  isSkeleton,
  testId,
}: ScreenHeaderPersistentTopBarActionProps) {
  isSkeleton = useIsSkeleton(isSkeleton)
  const { contentScrollProgress } = useScreenHeaderContext()

  const opacity = contentScrollProgress
  const pointerEvents = useTransform(opacity, [0, 0.2], ['none', 'auto'])
  const [inert, setInert] = useState(opacity.get() < 0.2)
  useMotionValueEvent(
    useTransform(opacity, [0, 0.2], [true, false]),
    'change',
    useCallback((v) => startTransition(() => setInert(v)), []),
  )

  return (
    <m.div
      inert={inert}
      className={styles.topBarAction}
      style={{
        opacity,
        pointerEvents,
      }}
    >
      <Button
        isSkeleton={isSkeleton}
        isDisabled={isDisabled}
        label={label}
        onPress={onPress}
        variant="text-inline-navigation"
        testId={testId}
      />
    </m.div>
  )
}

interface ScreenHeaderContentProps {
  children?: ReactNode
}

/**
 * Primary header content to display at the top of the page.
 * Note that this does not render a semantic `<header>` tag, you should
 * do that yourself.
 */
export function ScreenHeaderContent({ children }: ScreenHeaderContentProps) {
  const { connectContentRef, isOverlay, topBarHeight } =
    useScreenHeaderContext()

  // We need 2 divs here because Mobile Safari includes the padding in the
  // size calculation whereas other browsers do not.
  return (
    <>
      {!isOverlay && (
        // Adds spacing to account for the top bar
        <div
          style={{
            height: topBarHeight,
          }}
        />
      )}

      <div ref={connectContentRef}>{children}</div>
    </>
  )
}

interface ScreenHeaderTitleProps {
  children?: ReactNode
}

/**
 * Title container for the screen header content.
 * Does not contain any styling, but is necessary to coordinate animations.
 */
export function ScreenHeaderTitle({ children }: ScreenHeaderTitleProps) {
  const { connectTitleRef } = useScreenHeaderContext()

  return <m.div ref={connectTitleRef}>{children}</m.div>
}

export interface ScreenHeaderBorderProps {
  /**
   * When the border should be visible.
   * - `auto`: only when scrolled past the content.
   * - `always`: always visible.
   * Defaults to `auto`, unless {@link ScreenHeaderProps.scrollFading} is
   * set to `always`, in which case the border will not appear.
   */
  visibility?: 'auto' | 'always'
}

/**
 * Border that animates in as the content leaves the viewport.
 */
export function ScreenHeaderBorder({
  visibility = 'auto',
}: ScreenHeaderBorderProps) {
  const ref = useRef<HTMLDivElement>(null)
  const {
    scrollFading,
    isOverlay,
    contentScrollY,
    contentHeight,
    topBarHeight,
  } = useScreenHeaderContext()

  // For visibility: auto
  const opacityProgress = useElementScrollProgressInternal(
    contentScrollY,
    contentHeight,
    topBarHeight,
    isOverlay,
  )

  const opacity = useTransform(() =>
    visibility === 'always'
      ? 1
      : scrollFading === 'auto'
        ? opacityProgress.get()
        : 0,
  )

  return (
    <m.div
      ref={ref}
      aria-hidden
      className={styles.border}
      style={{
        opacity,
      }}
    />
  )
}

////////////
// Context
////////////

interface ScreenHeaderContextValue {
  isOverlay: boolean
  scrollFading: ScrollFading
  fadeVariant: ScrollFadeVariant

  viewportRef: RefObject<HTMLElement | null> | undefined

  connectMainRef: RefCallback<Element>
  connectTopBarRef: RefCallback<Element>
  connectTitleRef: RefCallback<Element>
  connectContentRef: RefCallback<Element>

  /**
   * Total height of the entire screen header.
   */
  mainHeight: number
  topBarHeight: number
  titleHeight: number
  contentHeight: number

  topOffset: number
  topBarOffset: number
  totalOffset: number

  safeAreaInsetTop: number

  titleScrollY: MotionValue<number>
  titleScrollProgress: MotionValue<number>
  contentScrollY: MotionValue<number>
  contentScrollProgress: MotionValue<number>
}

/**
 * Screen header context.
 */
const ScreenHeaderContext = createContext<ScreenHeaderContextValue | null>(null)

/**
 * Use the screen header context.
 */
function useScreenHeaderContext() {
  const ctx = useContext(ScreenHeaderContext)
  if (!ctx) {
    throw new Error('No screen header context available')
  }

  return ctx
}

/**
 * Motion value that tracks an elements scroll progress based on
 * the known size of the top bar.
 *
 * @param elementScrollY Motion value for the element's scroll position.
 * @param elementHeight Known height of the element.
 * @param topBarHeight Known height of the top bar.
 * @param isOverlay Whether the top bar should overlay the content.
 * @param offset An offset to apply.
 */
function useElementScrollProgressInternal(
  elementScrollY: MotionValue<number>,
  elementHeight: number,
  topBarHeight: number,
  isOverlay: boolean,
  offset = 0,
) {
  // Determine the scroll positions at which progress starts
  // and ends.
  let start = elementHeight === 0 ? 0 : elementHeight + offset
  let end = elementHeight + topBarHeight + offset

  if (isOverlay) {
    start -= topBarHeight
    end -= topBarHeight
  }

  return useTransform(
    elementScrollY,
    [start < 0 ? 0 : start, end < 1 ? 1 : end],
    [0, 1],
  )
}

/**
 * Motion value that tracks an element in the top bar's scroll progress
 * relative to the top bar.
 *
 * @param element The element to base the progress off of. If `null`, the
 *  returned motion value will be zero.
 *
 * @param offset An offset to apply.
 */
export function useTopBarElementScrollProgress(
  element: 'title' | 'content' | null,
  offset = 0,
) {
  const {
    topBarHeight,
    titleScrollY,
    contentScrollY,
    titleHeight,
    contentHeight,
    isOverlay,
  } = useScreenHeaderContext()

  const zeroMotionValue = useMotionValue(0)
  const [scrollY, height] = !element
    ? [zeroMotionValue, 1]
    : element === 'title'
      ? [titleScrollY, titleHeight]
      : [contentScrollY, contentHeight]
  return useElementScrollProgressInternal(
    scrollY,
    height,
    topBarHeight,
    isOverlay,
    offset,
  )
}

/**
 * Gets the total offset for the screen header, can be used
 * for positioning elements inside {@link ScreenHeaderProvider}
 * but outside {@link ScreenHeaderMain}.
 */
export function useScreenHeaderTotalOffset(): number {
  return useScreenHeaderContext().totalOffset
}

/**
 * Context set by the screen header top bar to allow children to
 * portal their overlays to the top bar container.
 *
 * Popovers like tooltips don't play well with position sticky by default.
 * When rendering a button with a tooltip in the screen header's top bar,
 * when the tooltip is open and the page scrolls, it will scroll with the page
 * rather than remain anchored to the button. By using the top bar as the
 * container instead, they will be positioned correctly.
 */
const ScreenHeaderTopBarPortalContainerContext = createContext<
  (() => HTMLElement | null) | undefined
>(undefined)

interface ScreenHeaderTopBarReactAriaPortalProviderProps {
  children?: ReactNode
}

/**
 * @see {ScreenHeaderTopBarPortalContainerContext}
 */
export function ScreenHeaderTopBarReactAriaPortalProvider({
  children,
}: ScreenHeaderTopBarReactAriaPortalProviderProps) {
  const getContainer = useContext(ScreenHeaderTopBarPortalContainerContext)
  if (getContainer === undefined) {
    return children
  }
  return (
    <UNSAFE_PortalProvider getContainer={getContainer}>
      {children}
    </UNSAFE_PortalProvider>
  )
}
